import { isPastAsOf } from '@/lib/asof/asof';

/**
 * =============================================================================
 *  staleTime 政策（ARCHITECTURE §10.2）。
 *
 *  **`as_of` 是過去日期時，staleTime 一律 Infinity。**
 *  理由不是效能，是語意：一個過去日期的視圖在**構造上**是不可變的。
 *  `bars_asof(as_of = 2015-06-30)` 今天問跟明天問，回的必須是同一組數字——
 *  如果不是，那不是快取失效，是 PIT 牆破了，而重新抓一次只會把破洞藏起來。
 *
 *  `staleTime` 由 registry 的 `freq` 推導，不是猜的：
 *  一個季度資料的 metric 每 5 分鐘 refetch 一次，是在對後端說謊說它可能變了。
 * ========================================================================== */

export type Freq = 'daily' | 'weekly' | 'monthly' | 'quarterly';

/** §10.2 的那張表，逐字。 */
export const STALE_BY_FREQ: Record<Freq, number> = {
  daily: 5 * 60_000,
  weekly: 60 * 60_000,
  monthly: 6 * 3_600_000,
  quarterly: 12 * 3_600_000,
};

export const STALE_DEFAULT = STALE_BY_FREQ.daily;

/**
 * @param asOf  目前的時光機位置（undefined ＝ 沒有倒帶）
 * @param today 市場當地的今天（marketToday()）——**不是**瀏覽器的今天
 * @param freq  registry 的 freq；未知時退回 daily
 */
export function staleTimeFor(
  asOf: string | undefined,
  today: string,
  freq: Freq | undefined = 'daily',
): number {
  if (isPastAsOf(asOf, today)) return Number.POSITIVE_INFINITY;
  return STALE_BY_FREQ[freq ?? 'daily'] ?? STALE_DEFAULT;
}

/**
 * gcTime：倒帶中的視圖值得在記憶體裡留久一點——使用者倒帶時通常會在
 * 幾個日期之間來回比對，每一次回頭都重抓會讓「倒帶」感覺像「重新載入」。
 */
export function gcTimeFor(asOf: string | undefined, today: string): number {
  return isPastAsOf(asOf, today) ? 24 * 3_600_000 : 30 * 60_000;
}
